/**
 * Home - the index route inside AppShell.
 *
 * Composition of LIVE operational signal cards. The 3-module
 * navigation grid that used to sit at the bottom of this page was
 * removed at P66 follow-on: the AppShell topnav already carries
 * the same three module entry points (Foundation / Operational
 * Twin / Framework + CAP) + the operational signal cards push
 * them too far down the page to be useful as navigation. The
 * header prose names the modules; the topnav is the navigation
 * spine.
 *
 * Slots (composition root fills these from App.tsx, since shared/
 * cannot import twin/ or framework/):
 *
 *   bannerSlot   -- top-left live operational tile (CascadeHomeCard)
 *   metricsSlot  -- top-right live operational tile (ResilienceTrendCard)
 *   mvbSlot      -- full-width MVB scorecard card
 *   adminSlot    -- admin-only full-width tile column (Board snapshot
 *                   + audit dossier). Component-internal admin gates
 *                   decide whether to render anything.
 *
 * Copy: shared/i18n/en.ts -> home.heading + home.body.
 * Layout: gap-* utilities only -- RTL-clean out of the box.
 */
import type { ReactNode } from "react";

import { useMe } from "shared/api/useMe";
import { GettingStartedTeaser } from "shared/onboarding/GettingStartedTeaser";

export interface HomeProps {
  /** Live operational tile lifted in from the composition root.
   *  P2.6.f mounts `<CascadeHomeCard />` here via App.tsx. */
  bannerSlot?: ReactNode;
  /** Second operational tile -- P2.9.c lands ResilienceTrendCard
   *  here. Renders beside `bannerSlot` at lg breakpoint, stacks
   *  on smaller. */
  metricsSlot?: ReactNode;
  /** MVB scorecard tile (P7.4). Renders full-width below the
   *  operational tile row. The card itself handles the empty /
   *  loading / error / active states so the composition root can
   *  mount unconditionally. */
  mvbSlot?: ReactNode;
  /** Admin-only surface (P3.4 lands DossierCard here). Rendered
   *  full-width BELOW the operational tile row. The card itself
   *  decides whether to render (admin-gated via useMe) so the
   *  composition root can mount unconditionally; non-admins see
   *  nothing where this slot would otherwise sit. */
  adminSlot?: ReactNode;
  /** P214.5 (Observatory): the position-queue pills (light tone),
   *  relocated off the navy header band into the Home daylight ribbon.
   *  Framework-coupled (CAP counts) so the composition root fills it
   *  with `<PositionQueueWidget tone="light" />`. Shares the onboarding
   *  ribbon row. */
  queueSlot?: ReactNode;
}

export function Home({
  bannerSlot,
  metricsSlot,
  mvbSlot,
  adminSlot,
  queueSlot,
}: HomeProps = {}) {
  // P272.5 (defect 11): every card in `adminSlot` gates its own render on
  // `useMe` (personal inbox / role- or admin-scoped surfaces), so before
  // /me settles they each return null then pop in one by one -- the page
  // visibly "assembles" after the always-on cards. Hold the whole
  // personalised block until /me resolves so it appears as one coherent
  // unit (and non-privileged users never mount its queries). The always-on
  // banner/metrics/mvb tiles still paint immediately. Gate on !isLoading
  // (not data) so a failed /me still unblocks -- the cards each null-check
  // me.data themselves.
  const meSettled = !useMe().isLoading;
  // P214.4 (Observatory): the page heading moved up into the night-sky
  // hero (AppShell heroSlot -> twin/home/HomeHero). The daylight body
  // below the sky carries the onboarding ribbon + the card grid, on the
  // reference's uniform 18px rhythm (P214.6). The full 12-col card
  // mosaic (mvb+board side by side, the assurance/benchmark trio) is
  // deferred -- it needs the bundled adminSlot fragment split into
  // per-card spans, a slot-API change beyond this restyle.
  return (
    <div className="space-y-[18px]">
      <GettingStartedTeaser trailing={queueSlot} />

      {(bannerSlot || metricsSlot) && (
        <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-2 items-stretch">
          {bannerSlot && <div className="h-full">{bannerSlot}</div>}
          {metricsSlot && <div className="h-full">{metricsSlot}</div>}
        </div>
      )}

      {mvbSlot && <div>{mvbSlot}</div>}

      {adminSlot && meSettled && <div className="space-y-[18px]">{adminSlot}</div>}
    </div>
  );
}
